import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaChartBar, FaEdit, FaList, FaPlus, FaTasks, FaTrash } from 'react-icons/fa';
import api from '../../../api/apiClient';

const initialForm = {
  title: '',
  description: '',
  duration_minutes: 30,
  pass_percentage: 40,
  negative_marking_enabled: false,
  negative_marking_percentage: 0
};

const OrgAssessmentList = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const response = await api.assessments.getOrgAssessments();
      setAssessments(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load assessments');
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessments();
  }, []);

  const handleOpenModal = (assessment = null) => {
    setEditing(assessment);
    setFormData(assessment ? {
      title: assessment.title || '',
      description: assessment.description || '',
      duration_minutes: assessment.duration_minutes || 30,
      pass_percentage: assessment.pass_percentage || 40,
      negative_marking_enabled: Boolean(assessment.negative_marking_enabled),
      negative_marking_percentage: assessment.negative_marking_percentage || 0
    } : initialForm);
    setShowModal(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Assessment title is required');
      return;
    }

    try {
      setSaving(true);
      if (editing) {
        await api.assessments.update(editing.id, formData);
        toast.success('Assessment updated successfully');
      } else {
        await api.assessments.create(formData);
        toast.success('Assessment created successfully');
      }
      setShowModal(false);
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this assessment?')) return;
    try {
      await api.assessments.delete(id);
      toast.success('Assessment deleted successfully');
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete assessment');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-4 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h5 className="mb-1 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Manage Assessments
          </h5>
          <p className="text-sm text-gray-600 mb-0">
            Create and manage assessments for your organization only.
          </p>
        </div>
        <Button
          bsPrefix="custom-hamburger"
          onClick={() => handleOpenModal()}
          className="border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg shadow-md text-sm flex items-center justify-center gap-2 p-2 px-3"
        >
          <FaPlus /> Add Assessment
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl shadow-lg bg-white">
        <table className="table table-bordered table-hover mb-0">
          <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <tr>
              <th>Title</th>
              <th>Duration</th>
              <th>Pass %</th>
              <th>Total Marks</th>
              <th>Enrolled</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assessments.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  No assessments created for your organization yet.
                </td>
              </tr>
            ) : (
              assessments.map((assessment) => (
                <tr key={assessment.id}>
                  <td className="font-medium">{assessment.title}</td>
                  <td>{assessment.duration_minutes} min</td>
                  <td>{assessment.pass_percentage}%</td>
                  <td>{assessment.total_marks || 0}</td>
                  <td>
                    <Badge bg={assessment.enrolled_count > 0 ? 'success' : 'secondary'}>
                      {assessment.enrolled_count || 0}
                    </Badge>
                  </td>
                  <td>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        className="p-1 border-0 text-purple-600 hover:bg-purple-50 rounded"
                        onClick={() => handleOpenModal(assessment)}
                        aria-label={`Edit ${assessment.title}`}
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="p-1 border-0 text-red-600 hover:bg-red-50 rounded"
                        onClick={() => handleDelete(assessment.id)}
                        aria-label={`Delete ${assessment.title}`}
                      >
                        <FaTrash />
                      </button>
                      <button
                        type="button"
                        className="p-1 border-0 text-blue-600 hover:bg-blue-50 rounded"
                        onClick={() => navigate(`/organization/assessments/${assessment.id}/questions`)}
                        aria-label={`Manage questions for ${assessment.title}`}
                      >
                        <FaList />
                      </button>
                      <button
                        type="button"
                        className="p-1 border-0 text-cyan-600 hover:bg-cyan-50 rounded"
                        onClick={() => navigate(`/organization/assessments/${assessment.id}/assignments`)}
                        aria-label={`Assign users for ${assessment.title}`}
                      >
                        <FaTasks />
                      </button>
                      <button
                        type="button"
                        className="p-1 border-0 text-emerald-600 hover:bg-emerald-50 rounded"
                        onClick={() => navigate(`/organization/assessments/${assessment.id}/analytics`)}
                        aria-label={`View analytics for ${assessment.title}`}
                      >
                        <FaChartBar />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <Modal.Title>{editing ? 'Edit Assessment' : 'Create Assessment'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={12}>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  aria-label="Assessment title"
                />
              </Col>
              <Col md={12}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  aria-label="Assessment description"
                />
              </Col>
              <Col md={6}>
                <Form.Label>Duration (minutes)</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, duration_minutes: Number(e.target.value) }))}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Pass Percentage</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.pass_percentage}
                  onChange={(e) => setFormData((prev) => ({ ...prev, pass_percentage: Number(e.target.value) }))}
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Enable Negative Marking"
                  checked={formData.negative_marking_enabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, negative_marking_enabled: e.target.checked }))}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Negative Marking (%)</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.negative_marking_percentage}
                  onChange={(e) => setFormData((prev) => ({ ...prev, negative_marking_percentage: Number(e.target.value) }))}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => setShowModal(false)}
            className="border-1 bg-gray-100 text-gray-700 border-gray-300 font-semibold rounded-lg text-sm p-2 px-3"
          >
            Cancel
          </Button>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleSubmit}
            disabled={saving}
            className="border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg text-sm p-2 px-3"
          >
            {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgAssessmentList;
